/** Post-paint budget for primary chrome destinations. Secondary CcLinks stay intent-only. */

export const PRIMARY_NAV_PREFETCH_MAX = 2;
export const PRIMARY_NAV_PREFETCH_CONCURRENCY = 1;
const IDLE_TIMEOUT_MS = 1000;

type PrefetchImpl = (href: string) => void | Promise<unknown>;
type ConnectionLike = { saveData?: boolean; effectiveType?: string };

const seen = new Set<string>();
const pending: string[] = [];
const promoted = new Set<string>();
const listeners = new Map<string, Set<() => void>>();

let prefetchImpl: PrefetchImpl | null = null;
let inFlight = 0;
let started = 0;
let gateOpened = false;
let gateArmed = false;
let pumpGeneration = 0;
let cancelGate: (() => void) | undefined;
const slotYieldCancels = new Set<() => void>();

export function setPrimaryNavPrefetchImpl(impl: PrefetchImpl | null): void {
  prefetchImpl = impl;
  if (impl && gateOpened) pump();
}

export function queuePrimaryNavPrefetch(href: string): void {
  const normalized = normalizePrimaryNavHref(href);
  if (!normalized || seen.has(normalized)) return;
  seen.add(normalized);
  if (isCurrentPathname(normalized)) return;
  if (started + pending.length >= PRIMARY_NAV_PREFETCH_MAX) return;
  pending.push(normalized);
  armPaintGate();
}

export function subscribePrimaryNavPrefetch(
  href: string,
  onPromote: () => void,
): () => void {
  const normalized = normalizePrimaryNavHref(href);
  queuePrimaryNavPrefetch(href);
  if (!normalized) return () => {};
  if (promoted.has(normalized)) {
    onPromote();
    return () => {};
  }
  let set = listeners.get(normalized);
  if (!set) {
    set = new Set();
    listeners.set(normalized, set);
  }
  set.add(onPromote);
  return () => {
    set.delete(onPromote);
    if (set.size === 0) listeners.delete(normalized);
  };
}

export function resetPrimaryNavPrefetchForTests(): void {
  seen.clear();
  pending.length = 0;
  promoted.clear();
  listeners.clear();
  prefetchImpl = null;
  inFlight = 0;
  started = 0;
  gateOpened = false;
  gateArmed = false;
  pumpGeneration += 1;
  cancelGate?.();
  cancelGate = undefined;
  const cancels = [...slotYieldCancels];
  slotYieldCancels.clear();
  for (const cancel of cancels) cancel();
}

export function normalizePrimaryNavHref(href: string): string | null {
  const raw = href.trim();
  if (!raw) return null;
  if (/^(mailto|tel|sms|javascript):/i.test(raw)) return null;

  if (/^(?:[a-z][a-z+\-.]*:)?\/\//i.test(raw)) {
    try {
      const absolute = new URL(raw.startsWith("//") ? `https:${raw}` : raw);
      if (typeof window === "undefined" || absolute.origin !== window.location.origin) {
        return null;
      }
      if (isApiPath(absolute.pathname)) return null;
      return `${absolute.pathname}${absolute.search}`;
    } catch {
      return null;
    }
  }

  const withoutHash = raw.split("#")[0] ?? "";
  if (!withoutHash.startsWith("/")) return null;
  const pathname = withoutHash.split("?")[0] ?? "";
  if (isApiPath(pathname)) return null;
  return withoutHash;
}

function isApiPath(pathname: string): boolean {
  return pathname === "/api" || pathname.startsWith("/api/");
}

function isCurrentPathname(normalized: string): boolean {
  if (typeof window === "undefined") return false;
  return (normalized.split("?")[0] || "/") === window.location.pathname;
}

function isRestrictedConnection(): boolean {
  if (typeof navigator === "undefined") return false;
  const connection = (navigator as Navigator & { connection?: ConnectionLike }).connection;
  return Boolean(
    connection?.saveData || /^(slow-)?2g$/.test(connection?.effectiveType ?? ""),
  );
}

function promote(href: string): void {
  promoted.add(href);
  listeners.get(href)?.forEach((listener) => listener());
}

function isThenable(value: unknown): value is PromiseLike<unknown> {
  return value != null && typeof (value as { then?: unknown }).then === "function";
}

type EntryCacheTiming = { serverTiming?: { name: string; description: string }[] };
export function canAutomaticallyWarmPrimary(entries?: EntryCacheTiming[]): boolean {
  const navigation = entries ?? (typeof performance !== "undefined" ? performance.getEntriesByType?.("navigation") as PerformanceNavigationTiming[] : undefined);
  const cache = navigation?.[0]?.serverTiming?.find(timing => timing.name === "cc-cache");
  return !cache || cache.description === "HIT" || cache.description === "STALE";
}

function pump(): void {
  if (!gateOpened || isRestrictedConnection() || !canAutomaticallyWarmPrimary()) return;

  const generation = pumpGeneration;
  while (
    inFlight < PRIMARY_NAV_PREFETCH_CONCURRENCY &&
    pending.length > 0 &&
    started < PRIMARY_NAV_PREFETCH_MAX
  ) {
    const href = pending.shift();
    if (!href) break;
    if (isCurrentPathname(href) || isRestrictedConnection()) continue;
    started += 1;
    inFlight += 1;
    promote(href);
    const implResult = prefetchImpl?.(href);
    const implIsThenable = isThenable(implResult);
    let implSettled = !implIsThenable;
    let yieldSettled = false;

    const tryRelease = () => {
      if (!implSettled || !yieldSettled || generation !== pumpGeneration) return;
      inFlight -= 1;
      pump();
    };

    if (implIsThenable) {
      void Promise.resolve(implResult).then(
        () => {
          implSettled = true;
          tryRelease();
        },
        () => {
          implSettled = true;
          tryRelease();
        },
      );
    }

    scheduleSlotYield(() => {
      yieldSettled = true;
      tryRelease();
    });
  }
}

/** Hold the concurrency slot past a void impl until a later idle/rAF — not the next microtask. */
function scheduleSlotYield(onYield: () => void): void {
  let settled = false;
  let cancelTimer: (() => void) | undefined;

  const finish = () => {
    if (settled) return;
    settled = true;
    slotYieldCancels.delete(cancel);
    onYield();
  };

  const cancel = () => {
    if (settled) return;
    settled = true;
    slotYieldCancels.delete(cancel);
    cancelTimer?.();
  };

  slotYieldCancels.add(cancel);

  if (typeof window === "undefined") {
    finish();
    return;
  }

  const requestIdle = window.requestIdleCallback;
  if (typeof requestIdle === "function") {
    const idleId = requestIdle(() => finish(), { timeout: IDLE_TIMEOUT_MS });
    cancelTimer = () => window.cancelIdleCallback?.(idleId);
    return;
  }
  if (typeof window.requestAnimationFrame === "function") {
    const rafId = window.requestAnimationFrame(() => finish());
    cancelTimer = () => window.cancelAnimationFrame(rafId);
    return;
  }
  const timeoutId = window.setTimeout(finish, 0);
  cancelTimer = () => window.clearTimeout(timeoutId);
}

function armPaintGate(): void {
  if (gateOpened) {
    pump();
    return;
  }
  if (gateArmed || typeof window === "undefined" || typeof document === "undefined") {
    return;
  }
  gateArmed = true;
  cancelGate = waitForPaintGate(() => {
    gateOpened = true;
    pump();
  });
}

function waitForPaintGate(onReady: () => void): () => void {
  let cancelled = false;
  let idleId: number | undefined;
  let timeoutId: number | undefined;

  const runIdle = () => {
    if (cancelled) return;
    const requestIdle = window.requestIdleCallback;
    if (typeof requestIdle === "function") {
      idleId = requestIdle(() => {
        if (!cancelled) onReady();
      }, { timeout: IDLE_TIMEOUT_MS });
      return;
    }
    timeoutId = window.setTimeout(() => {
      if (!cancelled) onReady();
    }, 0);
  };

  if (document.readyState === "complete") {
    runIdle();
  } else {
    window.addEventListener("load", runIdle, { once: true });
  }

  return () => {
    cancelled = true;
    window.removeEventListener("load", runIdle);
    if (idleId != null) window.cancelIdleCallback?.(idleId);
    if (timeoutId != null) window.clearTimeout(timeoutId);
  };
}
